import { useCallback, useEffect, useRef, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * Honeypot + fill-time signals. The backend treats a filled honeypot or an
 * impossibly fast submit as a bot.
 */
export const useBotProtection = () => {
  const startedAt = useRef(Date.now());
  const [honeypot, setHoneypot] = useState('');

  const getSignals = useCallback(() => ({
    website: honeypot,
    form_time: Date.now() - startedAt.current,
  }), [honeypot]);

  const honeypotField = (
    <div
      aria-hidden="true"
      style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}
    >
      <label htmlFor="website">Website</label>
      <input
        type="text"
        id="website"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
      />
    </div>
  );

  return { honeypotField, getSignals };
};

const loadTurnstileScript = () => {
  if (window.turnstile) return Promise.resolve();
  const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT}"]`);
  if (existing) {
    return new Promise((resolve) => existing.addEventListener('load', resolve, { once: true }));
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

/**
 * Renders Cloudflare Turnstile only when the backend exposes a site key.
 * Managed mode is invisible for most visitors.
 */
export const TurnstileWidget = ({ onToken, resetKey }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [siteKey, setSiteKey] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/public/security-config`, { headers: { Accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : {}))
      .then((data) => setSiteKey(data.turnstile_site_key || null))
      .catch(() => setSiteKey(null));
  }, []);

  useEffect(() => {
    if (!siteKey || !containerRef.current) return undefined;
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          appearance: 'interaction-only',
          callback: (token) => onToken(token),
          'expired-callback': () => onToken(''),
          'error-callback': () => onToken(''),
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, onToken]);

  useEffect(() => {
    if (resetKey && widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      onToken('');
    }
  }, [resetKey, onToken]);

  if (!siteKey) return null;
  return <div ref={containerRef} style={{ margin: '0 0 1rem', display: 'flex', justifyContent: 'center' }} />;
};
